import React from 'react';
import { Icon } from './Icon';
import { MessageSquare } from 'lucide-react';
import { Z } from '../constants/tokens';

/**
 * The app's replacement for window.prompt().
 *
 * Two modes:
 *
 *   text   — a normal input. Resolves the entered string, or null if cancelled.
 *   choice — pass `choices: [{ value, label }]` and the user picks from a list.
 *
 * Choice mode exists because every "which record do you want to KEEP?" prompt
 * in this app rendered a numbered list as text and asked the user to type the
 * number, then parsed it back with parseInt. That round trip is where the
 * off-by-one and NaN bugs lived (a non-numeric answer passed the range check
 * and threw on the index). Picking a value directly removes the parsing, and
 * therefore removes the bug class.
 *
 * Resolves null on cancel, matching window.prompt, so existing
 * `if (!answer) return;` guards keep working unchanged.
 */
export default function PromptDialog({
  message,
  title = 'Enter a value',
  defaultValue = '',
  placeholder = '',
  choices = null,
  confirmLabel = 'OK',
  cancelLabel = 'Cancel',
  allowEmpty = false,
  theme = {},
  onResolve,
}) {
  const isChoice = Array.isArray(choices) && choices.length > 0;
  const [value, setValue] = React.useState(
    isChoice ? (defaultValue !== '' && defaultValue != null ? defaultValue : choices[0].value) : defaultValue
  );
  const dialogRef = React.useRef(null);
  const firstFieldRef = React.useRef(null);
  const accent = theme.glossaryAccent || '#3b82f6';

  React.useEffect(() => {
    const opener = document.activeElement;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onResolve(null); return; }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const f = dialogRef.current.querySelectorAll('button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown, true);
    // Focus the field, not Cancel: unlike a confirm, the user came here to type
    // or choose, and nothing is destroyed by the dialog merely being open.
    firstFieldRef.current?.focus();
    if (!isChoice) firstFieldRef.current?.select?.();
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, [onResolve, isChoice]);

  // A required field with an empty box can't be submitted. Say so by disabling
  // the button rather than letting it look live and do nothing when clicked.
  // Optional fields ("reason (optional)", "leave blank for default") pass
  // allowEmpty and stay enabled, because for those an empty value is a real
  // answer rather than a missing one.
  const blocked = !isChoice && !allowEmpty && !String(value ?? '').trim();

  const submit = () => {
    if (blocked) return;
    onResolve(value);
  };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: '7px',
    border: `1px solid ${theme.border || '#e5e7eb'}`, background: theme.inputBg || '#fff',
    color: theme.text || '#111', fontSize: '0.85rem', fontFamily: 'inherit',
  };

  return (
    <div
      onClick={() => onResolve(null)}
      style={{
        position: 'fixed', inset: 0, zIndex: Z.dialog,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px', backgroundColor: 'rgba(0,0,0,0.55)',
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="gwv-prompt-title"
        tabIndex={-1}
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '460px', outline: 'none',
          background: theme.sidebar || theme.card || '#fff',
          border: `1px solid ${theme.border || '#e5e7eb'}`,
          borderRadius: '14px', boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
          display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 48px)',
        }}
      >
        <div style={{ display: 'flex', gap: '12px', padding: '18px 20px 14px', overflowY: 'auto' }}>
          <div style={{ flexShrink: 0, marginTop: '1px' }}>
            <Icon icon={MessageSquare} size={19} color={accent} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div id="gwv-prompt-title" style={{ fontWeight: 700, fontSize: '0.95rem', color: theme.text || '#111', marginBottom: '5px' }}>
              {title}
            </div>
            {message && (
              <div dir="auto" style={{ fontSize: '0.82rem', color: theme.textSub || '#555', lineHeight: 1.55, whiteSpace: 'pre-wrap', marginBottom: '10px' }}>
                {message}
              </div>
            )}

            {isChoice ? (
              <div role="radiogroup" aria-label={title} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {choices.map((c, i) => {
                  const selected = value === c.value;
                  return (
                    <label key={String(c.value)} dir="auto"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '9px', cursor: 'pointer',
                        padding: '9px 11px', borderRadius: '8px', fontSize: '0.82rem',
                        minHeight: '40px', boxSizing: 'border-box',
                        color: selected ? accent : (theme.text || '#111'),
                        background: selected ? `${accent}14` : 'transparent',
                        border: `1px solid ${selected ? `${accent}66` : (theme.border || '#e5e7eb')}`,
                        fontWeight: selected ? 600 : 400,
                      }}>
                      <input
                        ref={i === 0 ? firstFieldRef : null}
                        type="radio"
                        name="gwv-prompt-choice"
                        checked={selected}
                        onChange={() => setValue(c.value)}
                        style={{ accentColor: accent, flexShrink: 0 }}
                      />
                      <span style={{ minWidth: 0 }}>{c.label}</span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <input
                ref={firstFieldRef}
                type="text"
                value={value ?? ''}
                placeholder={placeholder}
                onChange={e => setValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
                aria-label={title}
                dir="auto"
                style={inputStyle}
              />
            )}
          </div>
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: '8px',
          padding: '12px 20px', borderTop: `1px solid ${theme.border || '#e5e7eb'}`, flexShrink: 0,
        }}>
          <button type="button" onClick={() => onResolve(null)}
            style={{
              padding: '8px 16px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600,
              background: 'transparent', color: theme.textSub || '#555',
              border: `1px solid ${theme.border || '#e5e7eb'}`, cursor: 'pointer',
            }}>
            {cancelLabel}
          </button>
          <button type="button" onClick={submit} disabled={blocked}
            title={blocked ? 'Enter a value to continue' : undefined}
            style={{
              padding: '8px 16px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 700,
              background: blocked ? (theme.card || '#e5e7eb') : accent,
              color: blocked ? (theme.textSub || '#888') : '#fff',
              border: blocked ? `1px solid ${theme.border || '#e5e7eb'}` : 'none',
              cursor: blocked ? 'not-allowed' : 'pointer',
            }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
